
import { defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import Container from '@/components/form/container/Container.vue'
import { useI18n } from 'vue-i18n'
import { formatDate, formatThousands } from '@/utils'
import { useRoute } from 'vue-router'
import {
  getPaymentAcceptanceHistory, getPaymentSlipHistoryCashDetail, getRelatedPaymentSlipPageList
} from '@/apis/paymentSlip/paymentSlipHistory'
import AcceptanceDetailDialog from '@/views/pay/AcceptanceDetailDialog.vue'
import {
  PaymentAcceptanceHistoryModel,
  PaymentAcceptanceHistoryParamModel, PaymentSlipHistoryModel, RelatedPaymentSlipPageParamModel
} from '@/model/paymentSlip/paymentSlipHistoryModel'
import { formatBanendFlag, formatSplitFlag } from '@/utils/acceptanceBillFormatUtil'
import { PaymentSlipModel } from '@/model/paymentSlip/paymentSlipModel'
import { ElMessage } from 'element-plus'

export default defineComponent({
  name: 'PaymentSlipHistoryAcceptanceDetail',
  methods: { formatThousands },
  props: {
    labelWidth: {
      type: String,
      default: '680px'
    }
  },
  components: {
    AcceptanceDetailDialog,
    SimpleTable,
    Container
  },
  setup() {
    const { t } = useI18n()
    const id = useRoute().params.id.toString()
    const tableRef = ref()
    const detailTableStruct = [
      { prop: 'draftNo', label: t('Acceptance.draftNo') },
      { prop: 'splitFlag', label: t('Acceptance.splitFlag'), formatter: (row: any) => formatSplitFlag(row) },
      { prop: 'issueDate', label: t('Acceptance.issueDate'), formatter: (row: any) => formatDate(row.issueDate, 'yyyy-MM-dd') },
      { prop: 'dueDate', label: t('Acceptance.dueDateTime'), formatter: (row: any) => formatDate(row.dueDate, 'yyyy-MM-dd') },
      { prop: 'bankName', label: t('Acceptance.bankName') },
      { prop: 'banendFlag', label: t('Acceptance.banendFlagTrue'), formatter: (row: any) => formatBanendFlag(row.banendFlag) },
      { prop: 'draftAmt', label: t('Acceptance.draftAmt'), formatter: (row: any) => formatThousands(row.draftAmt), align: 'right' },
      { prop: 'useAmt', label: t('pay.usedAmount'), formatter: (row: any) => formatThousands(row.useAmt), align: 'right' }
    ]
    const paymentSlipTableStruct = [
      { prop: 'id', label: t('pay.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'payee', label: t('pay.payee') },
      { prop: 'reference', label: t('pay.reference') },
      { prop: 'amount', label: t('pay.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'currency', label: t('pay.currency') },
      { prop: 'dueDate', label: t('order.cutOffDate'), formatter: (row: any) => formatDate(row.dueDate, 'yyyy-MM-dd') },
      { prop: 'comments', label: t('pay.comments') },
      {
        prop: 'paymentDate',
        label: t('pay.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd')
      },
      { prop: 'purpose', label: t('pay.purposeView'), width: 170 }
    ]
    const dataMap = reactive({
      tableData: Array<PaymentAcceptanceHistoryModel>(),
      columnStruct: detailTableStruct,
      listLoading: true,
      detailData: {} as PaymentSlipHistoryModel,
      acceptanceDetailData: {} as PaymentAcceptanceHistoryModel,
      acceptanceDetailDialogVisible: false,
      limit: 10,
      total: 0,
      page: 1,
      cashListLoading: true,
      cashTableData: Array<PaymentSlipModel>(),
      cashColumnStruct: paymentSlipTableStruct,
      async getData() {
        dataMap.listLoading = true
        const data = await getPaymentSlipHistoryCashDetail(id)
        if (data && data.data) {
          dataMap.detailData = data.data
        }
        const params: PaymentAcceptanceHistoryParamModel = {
          trnUid: dataMap.detailData.trnUid
        }
        const acceptanceHistoryData = await getPaymentAcceptanceHistory(params)
        if (acceptanceHistoryData && acceptanceHistoryData.data) {
          dataMap.tableData = acceptanceHistoryData.data
        }
        tableRef.value.clearSelection()
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      paymentDateFormat(paymentDate: Date): string {
        return formatDate(paymentDate, 'yyyy-MM-dd')
      },
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true
        let historyId
        try {
          historyId = Number(id)
        } catch (error) {
          ElMessage({
            message: 'id 格式错误;' + id,
            type: 'error',
            duration: 2000
          })
          return
        }
        const params: RelatedPaymentSlipPageParamModel = {
          id: historyId,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        const data = await getRelatedPaymentSlipPageList(params)
        if (data && data.data && data.data.list.length > 0) {
          dataMap.cashTableData = data.data.list
        } else {
          dataMap.cashTableData = []
        }
        dataMap.total = data?.data.totalCount ?? 0
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.cashListLoading = false
        }, 0.5 * 1000)
      }
    })
    const viewDetail = async(row: any) => {
      // router.push({ path: `employeeUpdate/${row.id}` })
      dataMap.acceptanceDetailData = row
      dataMap.acceptanceDetailDialogVisible = true
    }
    onMounted(() => {
      dataMap.getData()
      dataMap.getPageList(null, 10)
    })
    return {
      t,
      tableRef,
      viewDetail,
      ...toRefs(dataMap)
    }
  }
})
